import { type RefSelectProps, Select } from 'antd'
import clsx from 'clsx'
import { forwardRef, useMemo, type Ref } from 'react'

import { type SingleOrMultipleSelectProps } from '@/components/types'
import { STATUSES } from '@/constants/common/message.constants'
import {
  type ShipmentOrderStatusType,
  SHIPMENT_ORDER_STATUS_TYPE,
  SHIPMENT_ORDER_STATUS_TYPE_LABEL
} from '@/libs/api/types'
import { useSelectOnChangeHandler } from '@/libs/hooks'

type Props = SingleOrMultipleSelectProps<
  ShipmentOrderStatusType,
  ShipmentOrderStatusTypeOption
> & {
  /**
   * 除外するオプションを指定する関数
   */
  excludeOptions?: (option: ShipmentOrderStatusTypeOption) => boolean
}

type ShipmentOrderStatusTypeOption = {
  label: string
  value: ShipmentOrderStatusType
}

const options: ShipmentOrderStatusTypeOption[] = Object.values(
  SHIPMENT_ORDER_STATUS_TYPE
).map((type) => ({
  label: SHIPMENT_ORDER_STATUS_TYPE_LABEL[type],
  value: type
}))

/**
 * 配送依頼ステータスを選択するSelect
 */
export const ShipmentOrderStatusTypeSelect = forwardRef(
  (
    {
      className,
      placeholder = STATUSES.UNSELECTED,
      value,
      mode,
      onChange,
      excludeOptions,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const handleChange = useSelectOnChangeHandler({ mode, onChange })

    const options_ = useMemo(() => {
      if (excludeOptions) {
        return options.filter((option) => !excludeOptions(option))
      }
      return options
    }, [excludeOptions])

    return (
      <Select
        ref={ref}
        {...props}
        mode={mode}
        value={value}
        onChange={handleChange}
        className={clsx(['min-w-[180px]', className])}
        options={options_}
        placeholder={placeholder}
      />
    )
  }
)
